import { useEffect } from 'react'

import {
  useGetTermsDigitalAccount,
  useGetTermsRemittanceAccount,
} from 'services/terms-and-conditions/useTermsAndConditions'
import { Term } from 'services/terms-and-conditions/types'
import { getUrlParams, normalizeLanguage } from 'utils'
import { TermsList } from 'components/TermsList'
import { useUserInfo } from 'hooks/useUserInfo'
import { useParams } from 'react-router-dom'
import { useAppInfo } from 'hooks/useAppInfo'
import { LanguagesEnum } from 'common/enums/languages'

import { interWbSession } from '@interco/inter-webview-bridge'

import { ErrorPage } from '../../components/ErrorPage'
import { errorData } from './data'

export function TermsAndConditionsList() {
  const { id } = useParams<{ id: string }>()
  const { institution, flow } = getUrlParams(window.location.href)
  const { userType } = useUserInfo()

  const appInfo = useAppInfo()
  const selectedLanguage = normalizeLanguage(appInfo?.language, LanguagesEnum.PT_BR)

  const isRemittance = (): boolean =>
    flow === 'ONBOARDING_REMITTANCE' || (flow === 'PROFILE' && userType === 'remittance_account')

  const isDigitalAccount = (): boolean =>
    flow === 'ONBOARDING_DIGITAL_ACCOUNT' || (flow === 'PROFILE' && userType === 'bank_account')

  const getInstitution = (): string => {
    if (flow === 'PROFILE') {
      return 'LOGGED_AREA'
    }
    return institution
  }

  const {
    data: remittanceData,
    isLoading: isRemittanceLoading,
    isError: isRemittanceError,
    refetch: refetchRemittance,
    error: remittanceError,
  } = useGetTermsRemittanceAccount({
    enabled: isRemittance(),
  })

  const {
    data: digitalAccountData,
    isLoading: isDigitalAccountLoading,
    isError: isDigitalAccountError,
    refetch: refetchDigitalAccount,
    error: digitalAccountError,
  } = useGetTermsDigitalAccount(getInstitution(), {
    enabled: isDigitalAccount(),
  })

  let data: Term[] = []
  let isLoading = false
  let isError = false
  let error: unknown
  let refetch: (() => void) | undefined

  if (isRemittance()) {
    data = remittanceData ?? []
    isLoading = isRemittanceLoading
    isError = isRemittanceError
    error = remittanceError
    refetch = refetchRemittance
  } else if (isDigitalAccount()) {
    data = digitalAccountData ?? []
    isLoading = isDigitalAccountLoading
    isError = isDigitalAccountError
    error = digitalAccountError
    refetch = refetchDigitalAccount
  }

  useEffect(() => {
    if (isError) {
      interWbSession.requestAnalytics('GLOBAL_ACCOUNT_WEBVIEW', {
        eventId: 'ERROR_GET_TERMS_AND_CONDITIONS_LIST',
        error: JSON.stringify(error),
        institution,
        flow,
        userType,
      })
    }
  }, [isError, error, institution, flow, userType])

  if (isError && refetch) {
    return (
      <ErrorPage
        title={errorData[selectedLanguage].title}
        description={errorData[selectedLanguage].description}
        primaryButton={errorData[selectedLanguage].primaryButton}
        secondaryButton={errorData[selectedLanguage].secondaryButton}
        refetch={refetch}
      />
    )
  }

  return <TermsList id={id} termsData={data} isLoading={isLoading} />
}
